/**
 * Utilidades para trabajar con thumbnails generados automáticamente por el backend
 *
 * El backend genera automáticamente estos tamaños:
 * - thumb: 150x150px (para listas/cards)
 * - small: 300x300px (para previews pequeños)
 * - medium: 600x600px (para vistas medianas)
 * - large: 1200x1200px (para vistas grandes)
 */

import React from 'react';

/**
 * Tamaños de thumbnail disponibles
 */
export const THUMBNAIL_SIZES = {
  THUMB: 'thumb', // 150x150
  SMALL: 'small', // 300x300
  MEDIUM: 'medium', // 600x600
  LARGE: 'large', // 1200x1200
};

/**
 * Construye la URL del thumbnail a partir de la URL original
 *
 * @param {string} originalUrl - URL de la imagen original
 * @param {string} size - Tamaño del thumbnail (THUMB, SMALL, MEDIUM, LARGE)
 * @returns {string} - URL del thumbnail
 *
 * @example
 * // Original: https://cdn.com/images/menu/item-123.jpg
 * // Thumb:    https://cdn.com/images/menu/item-123-thumb.webp
 * getThumbnailUrl(originalUrl, THUMBNAIL_SIZES.THUMB)
 */
export const getThumbnailUrl = (originalUrl, size = THUMBNAIL_SIZES.THUMB) => {
  if (!originalUrl) return null;

  // Si la URL ya es un thumbnail, retornarla tal cual
  if (isThumbnailUrl(originalUrl)) {
    return originalUrl;
  }

  try {
    // Remover la extensión y agregar el sufijo del tamaño
    const url = new URL(originalUrl);
    const pathname = url.pathname;
    const lastDotIndex = pathname.lastIndexOf('.');

    if (lastDotIndex === -1) {
      // No tiene extensión, agregar sufijo directamente
      url.pathname = `${pathname}-${size}.webp`;
    } else {
      // Tiene extensión, reemplazarla
      const pathWithoutExtension = pathname.substring(0, lastDotIndex);
      url.pathname = `${pathWithoutExtension}-${size}.webp`;
    }

    return url.toString();
  } catch (error) {
    console.error('[ThumbnailUtils] Error building thumbnail URL:', error);
    return originalUrl; // Fallback a la original
  }
};

/**
 * Verifica si una URL es de un thumbnail
 */
export const isThumbnailUrl = (url) => {
  if (!url) return false;

  const sizes = Object.values(THUMBNAIL_SIZES);
  return sizes.some((size) => url.includes(`-${size}.webp`));
};

/**
 * Obtiene la URL original desde una URL de thumbnail
 */
export const getOriginalUrl = (thumbnailUrl) => {
  if (!thumbnailUrl || !isThumbnailUrl(thumbnailUrl)) {
    return thumbnailUrl;
  }

  try {
    // Remover el sufijo del tamaño y restaurar la extensión original
    const sizes = Object.values(THUMBNAIL_SIZES);

    for (const size of sizes) {
      if (thumbnailUrl.includes(`-${size}.webp`)) {
        // Asumir que el original era .jpg (podríamos mejorar esto)
        return thumbnailUrl.replace(`-${size}.webp`, '.jpg');
      }
    }

    return thumbnailUrl;
  } catch (error) {
    console.error('[ThumbnailUtils] Error getting original URL:', error);
    return thumbnailUrl;
  }
};

/**
 * Hook de React para cargar una imagen con fallback
 * Intenta cargar el thumbnail, si falla usa la original
 */
export const useImageWithFallback = (originalUrl, thumbnailSize = THUMBNAIL_SIZES.THUMB) => {
  const [imageSrc, setImageSrc] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!originalUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const thumbnailUrl = getThumbnailUrl(originalUrl, thumbnailSize);

    // Intentar cargar el thumbnail primero
    const img = new Image();

    img.onload = () => {
      setImageSrc(thumbnailUrl);
      setIsLoading(false);
    };

    img.onerror = () => {
      console.log('[ThumbnailUtils] Thumbnail not found, using original:', thumbnailUrl);
      // Si el thumbnail no existe, usar la original
      setImageSrc(originalUrl);
      setIsLoading(false);
    };

    img.src = thumbnailUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [originalUrl, thumbnailSize]);

  return { imageSrc, isLoading, error };
};

/**
 * Componente de imagen optimizada con lazy loading y fallback
 */
export const OptimizedImage = ({
  src,
  alt,
  size = THUMBNAIL_SIZES.THUMB,
  className,
  style,
  loading = 'lazy',
}) => {
  const thumbnailUrl = getThumbnailUrl(src, size);

  return (
    <img
      src={thumbnailUrl || src}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      onError={(e) => {
        // Si el thumbnail falla, usar la original
        if (e.target.src !== src) {
          console.log('[OptimizedImage] Falling back to original:', src);
          e.target.src = src;
        }
      }}
    />
  );
};
